<template>
	<div class="pa-4 fill-height d-flex flex-column flex-grow-1">
		<AgGrid class="flex-grow-1" ref="ressourceAgGridRef" :fetchUrl="ressourceFetchUrl" :columnDefs="ressourceColumnDefs" :gridParams="ressourceGridParams" :refresh="ressourceRefresh" :gridOptions="ressourceGridOptions" :domLayout="domLayout" @on-grid-ready="onGridReady" />
	</div>
</template>

<script setup>
	import { computed, inject, onMounted, ref, watch, useTemplateRef, onBeforeMount } from "vue";
	import AgGrid from "@/components/AgGrid.vue";
	import countriesJson from "@/components/json/countries.json";
	import { toast } from "vue3-toastify";
	import { useLogStore } from "@/stores/logStore";
	import { useUserStore } from "@/stores/userStore";
import { ColumnMenuModule } from "ag-grid-enterprise";

	const props = defineProps({
		customerReference: {
			type: String,
			default: null,
		},
	});
	const emit = defineEmits(["pending-changes"]);

	const urlWebAPI = inject("urlWebAPI");
	const translate = inject("translate");
	const getFormBody = inject("getFormBody");

	const logStore = useLogStore();
	const userStore = useUserStore();

	const domLayout = "normal";

	const ressourceGridRef = useTemplateRef("ressourceAgGridRef");
	const ressourceFetchUrl = "GetApplicationRessources";
	const ressourceColumnDefs = ref([
		{ field: "application", rowGroup: true, hide: true },
		{
			field: "name",
			headerName: translate("name", "column"),
			sort: "asc",
			pinned: "left",
			lockPinned: true,
			
		},
		{
			field: "locales",
			headerName: translate("locales", "column"),
			children: getLocaleColumns(),
			
		},
	]);

	const ressourceGridParams = {
		token: userStore.getUser().token,
		customerReference: props.customerReference,
	};

	const ressourceGridOptions = {
		getRowId: (params) => params.data.application + "_" + params.data.id,
	};

	const ressourceRefresh = ref({
		state: false,
		type: "column",
	});

	const rowsUpdated = ref([]);

	const hasPendingChanges = computed(() => rowsUpdated.value.length > 0);

	watch(hasPendingChanges, (value) => {
		emit("pending-changes", value);
	});

	onBeforeMount(() => {});

	onMounted(() => {
		ressourceGridRef.value.gridRef.api.setGridOption("loading", true);
		ressourceGridRef.value.getGridData().then(() => {
			getLocaleNumber().then(() => {
				ressourceGridRef.value.gridRef.api.setGridOption("loading", false);
			});
		});
	});

	function onGridReady(params) {}

	function getLocaleColumns() {
		var localesColumns = [];
		var countries = countriesJson;

		countries.forEach((country) => {
			localesColumns.push({
				cellRenderer: "translationValueCellRenderer",
				cellEditor: "agTextCellEditor",
				field: country.code,
				headerName: `${country.language} (${country.code})`,
				headerComponentParams: {
					innerHeaderComponent: "localeInnerHeaderComponent",
					innerHeaderComponentParams: { countryCode: country.code },
				},
				editable: true,
				enableCellChangeFlash: true,
				type: "language",
				
			});

			localesColumns.push({
				minWidth: 55,
				maxWidth: 55,
				menuTabs: [],
				suppressFiltersToolPanel: true,
				suppressHeaderFilterButton: true,
				sortable: true,
				filter: false,
				cellRenderer: "translationValueCellRenderer",
				filterParams: {
					cellRenderer: "translationValueCellRenderer",
				},
				cellEditor: "agTextCellEditor",
				field: `isCustom${country.code}`,
				headerName: `${translate("isCustom", "column")} (${country.code})`,
				headerComponentParams: {
					innerHeaderComponent: "localeInnerHeaderComponent",
					innerHeaderComponentParams: { countryCode: country.code },
				},
				editable: false,
				type: "language",
				valueGetter: (params) => {
					if(params.data !== undefined){
						return Object.keys(params.data.localesCustom).indexOf(params.colDef.field.replace("isCustom", "").toLowerCase()) > -1 && params.data.localesCustom[params.colDef.field.replace("isCustom", "").toLowerCase()];
					}
				},
				
			});

			localesColumns.push({
				field: `localeId${country.code}`,
				headerName: `localeId ${country.code}`,
				editable: false,
				hide: true, //switch to false for debug
				enableCellChangeFlash: true,
				valueGetter: (params) => {
					return Object.keys(params.data.localeIds).indexOf(params.colDef.field.replace("localeId", "").toLowerCase()) > -1 && params.data.localeIds[params.colDef.field.replace("localeId", "").toLowerCase()];
				},
			});
		});

		return localesColumns;
	}

	async function getLocaleNumber() {
		var formBody = getFormBody(ressourceGridParams);

		return fetch(urlWebAPI + "GetLocaleNumber", {
			method: "POST",
			body: formBody,
			headers: {
				"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
			},
		})
			.then((result) => result.json())
			.then((locales) => {
				var updatedColumnDefs = ressourceColumnDefs.value.filter((rc) => rc.field != "locales");

				var localeColumnDef = ressourceColumnDefs.value.filter((rc) => rc.field == "locales").first();
				localeColumnDef.children = localeColumnDef.children.filter((c) => 
					locales.indexOf(c.field.toLowerCase()) > -1 || 
					locales.map((l) => "iscustom" + l).indexOf(c.field.toLowerCase()) > -1 || 
					locales.map((l) => "localeid" + l).indexOf(c.field.toLowerCase()) > -1);
				updatedColumnDefs.push(localeColumnDef);

				ressourceColumnDefs.value = updatedColumnDefs;

				ressourceRefresh.value = { type: "column", state: true };

				setTimeout(() => {
					ressourceRefresh.value = { ...ressourceRefresh.value, state: false };
				}, 1000);
			})
			.catch((error) => {
				logStore.log(error, "error");
				toast.error("Error during executing GetLocaleNumber data.");
			});
	}

	function save() {
		let data = {
			token: userStore.getUser().token,
			customerReference: props.customerReference,
			ressources: rowsUpdated.value,
		};

		$.postData(urlWebAPI + "SaveRessources", data)
			.then(result => {
				mergeResponse(result, rowsUpdated.value);
				rowsUpdated.value = [];
				toast.success(translate("customerParamsWellSaved", "toast"), { theme: "colored" });
			})
			.catch((error) => {
				logStore.log(error, "error");
				toast.error("Error during executing SaveRessources data.");
			});
	}

	function mergeResponse(apiResult, rowsUpdatedList) {

		const apiByRow = new Map();
		for (const api of apiResult) {
			const key = `${api.application}|${api.id}`;
			let list = apiByRow.get(key);
			if (!list) apiByRow.set(key, (list = []));
			list.push(api);
		}

		rowsUpdatedList.forEach((row) => {
			row.locales ??= {};
			row.localeIds ??= {};
			row.localesCustom ??= {};

			// only the locales returned by the API are touched, untouched locales keep their ids and flags
			for (const api of apiByRow.get(`${row.application}|${row.id}`) ?? []) {
				const locale = api.locale;
				if (api.deleted && !api.valueId) {
					// deleted and nothing replaces it: back to the "never had a value" state
					row.locales[locale] = "";
					delete row.localeIds[locale];
					delete row.localesCustom[locale];
				} else {
					// saved, or deleted with a standard fallback value
					row.locales[locale] = api.value;
					row.localeIds[locale] = api.valueId;
					row.localesCustom[locale] = api.isCustom;
				}
			}
		});

		const result = rowsUpdatedList;

		result.forEach((rowUpdated) => {
			//the key (rowUpdated.application+'_'+rowUpdated.id) used in getRowNode is defined in ressourceGridOptions => getRowId
			var rowNode = ressourceGridRef.value.gridRef.api.getRowNode(rowUpdated.application + "_" + rowUpdated.id);
			rowNode.updateData(rowUpdated);
		});
	}

	function undo() {
		var gridApi = ressourceGridRef.value.gridRef.api;

		for (var i = 0; i < gridApi.getCurrentUndoSize() + 1; i++)
			setTimeout(() => {
				gridApi.undoCellEditing();
			}, 10 * i);

		gridApi.redrawRows();
		rowsUpdated.value = [];
	}

// ViewManageCustomer.vue calls undo()/save() through a template ref, and AgGrid.vue reaches
// into rowsUpdated through the $parent chain — both only see what defineExpose() exposes.
	defineExpose({ undo, save, rowsUpdated });
</script>
